import { createFileRoute, Link } from "@tanstack/react-router"
import {
	IconArrowLeft,
	IconCheck,
	IconX,
	IconClock,
	IconCopy,
} from "@tabler/icons-react"
import { AppShell } from "@/components/layout/app-shell"
import { ProtectedRoute } from "@/components/layout/protected-route"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import {
	Card,
	CardContent,
	CardDescription,
	CardHeader,
	CardTitle,
} from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { useMerchantOrder } from "@/hooks/use-merchant-orders"

export const Route = createFileRoute("/orders/$orderId")({
	component: OrderDetailPage,
})

function OrderDetailPage() {
	const { orderId } = Route.useParams()

	return (
		<ProtectedRoute>
			<AppShell>
				<OrderDetailContent orderId={orderId} />
			</AppShell>
		</ProtectedRoute>
	)
}

const statusConfig: Record<
	string,
	{ label: string; icon: typeof IconCheck; variant: "default" | "secondary" | "destructive" | "outline"; color: string }
> = {
	completed: { label: "Completed", icon: IconCheck, variant: "default", color: "text-green-600" },
	pending: { label: "Pending", icon: IconClock, variant: "secondary", color: "text-yellow-600" },
	processing: { label: "Processing", icon: IconClock, variant: "outline", color: "text-blue-600" },
	failed: { label: "Failed", icon: IconX, variant: "destructive", color: "text-red-600" },
	expired: { label: "Expired", icon: IconX, variant: "destructive", color: "text-red-600" },
	cancelled: { label: "Cancelled", icon: IconX, variant: "outline", color: "text-gray-600" },
}

function OrderDetailContent({ orderId }: { orderId: string }) {
	const { data: order, isLoading, error } = useMerchantOrder(orderId)

	if (isLoading) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary" />
			</div>
		)
	}

	if (error || !order) {
		return (
			<div className="flex items-center justify-center h-64">
				<div className="text-center">
					<p className="text-destructive font-medium">Order not found</p>
					<Button asChild variant="outline" className="mt-4">
						<Link to="/orders">
							<IconArrowLeft className="h-4 w-4 mr-2" />
							Back to Orders
						</Link>
					</Button>
				</div>
			</div>
		)
	}

	const config = statusConfig[order.status] || statusConfig.pending
	const StatusIcon = config.icon

	const copyToClipboard = (text: string | undefined | null) => {
		if (text) {
			navigator.clipboard.writeText(text)
		}
	}

	return (
		<div className="space-y-6">
			<div className="flex items-center gap-4">
				<Button asChild variant="ghost" size="sm">
					<Link to="/orders">
						<IconArrowLeft className="h-4 w-4 mr-2" />
						Back to Orders
					</Link>
				</Button>
			</div>

			{/* Order Status Header */}
			<Card>
				<CardHeader>
					<div className="flex items-center justify-between">
						<div>
							<CardTitle className="text-2xl">{order.merchantOrderId}</CardTitle>
							<CardDescription>
								Created on {new Date(order.createdAt).toLocaleString()}
							</CardDescription>
						</div>
						<Badge variant={config.variant} className="text-sm px-3 py-1">
							<StatusIcon className="h-4 w-4 mr-1" />
							{config.label}
						</Badge>
					</div>
				</CardHeader>
			</Card>

			<div className="grid gap-6 md:grid-cols-2">
				{/* Order Details */}
				<Card>
					<CardHeader>
						<CardTitle>Order Details</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<DetailRow
							label="Merchant Order ID"
							value={order.merchantOrderId}
							onCopy={() => copyToClipboard(order.merchantOrderId)}
						/>
						<DetailRow label="Order Type" value={order.orderType === "collect" ? "Deposit" : "Payout"} />
						<DetailRow label="Amount" value={`৳${Number(order.amount).toLocaleString()}`} />
						<DetailRow label="Currency" value={order.currency} />
						<DetailRow label="Channel" value={order.channel} />
						<DetailRow
							label="Status"
							value={<span className={config.color}>{config.label}</span>}
						/>
						{order.paymentReference && (
							<DetailRow
								label="Payment Reference"
								value={order.paymentReference}
								onCopy={() => copyToClipboard(order.paymentReference)}
							/>
						)}
						{order.transactionId && (
							<DetailRow
								label="Transaction ID"
								value={order.transactionId}
								onCopy={() => copyToClipboard(order.transactionId)}
							/>
						)}
					</CardContent>
				</Card>

				{/* Player & SIM Info */}
				<Card>
					<CardHeader>
						<CardTitle>Payment Information</CardTitle>
					</CardHeader>
					<CardContent className="space-y-4">
						<DetailRow label="Player ID" value={order.playerId || "-"} />
						<DetailRow label="Player Phone" value={order.playerPhone || "-"} />
						<DetailRow label="Customer Phone" value={order.customerPhone || "-"} />
						<Separator />
						<DetailRow label="Assigned SIM" value={order.simPhoneNumber || "-"} />
						{order.commissionAmount && (
							<>
								<Separator />
								<DetailRow label="Commission" value={`৳${Number(order.commissionAmount).toLocaleString()}`} />
							</>
						)}
						{order.agentCommission && (
							<DetailRow label="Agent Commission" value={`৳${Number(order.agentCommission).toLocaleString()}`} />
						)}
					</CardContent>
				</Card>
			</div>

			{/* Timeline */}
			<Card>
				<CardHeader>
					<CardTitle>Timeline</CardTitle>
				</CardHeader>
				<CardContent>
					<div className="space-y-4">
						<TimelineItem
							label="Order Created"
							timestamp={order.createdAt}
							isFirst
						/>
						{order.matchedAt && (
							<TimelineItem
								label="Payment Matched"
								timestamp={order.matchedAt}
							/>
						)}
						{order.completedAt && (
							<TimelineItem
								label="Order Completed"
								timestamp={order.completedAt}
							/>
						)}
						<TimelineItem
							label="Expires"
							timestamp={order.expiresAt}
							isLast
						/>
					</div>
				</CardContent>
			</Card>
		</div>
	)
}

function DetailRow({
	label,
	value,
	onCopy,
}: {
	label: string
	value: React.ReactNode
	onCopy?: () => void
}) {
	return (
		<div className="flex items-center justify-between py-2">
			<span className="text-sm text-muted-foreground">{label}</span>
			<div className="flex items-center gap-2">
				<span className="text-sm font-medium">{value}</span>
				{onCopy && (
					<Button
						variant="ghost"
						size="sm"
						className="h-6 w-6 p-0"
						onClick={() => {
							try {
								onCopy()
							} catch (e) {
								console.error("Copy failed", e)
							}
						}}
					>
						<IconCopy className="h-3 w-3" />
					</Button>
				)}
			</div>
		</div>
	)
}

function TimelineItem({
	label,
	timestamp,
	isFirst = false,
	isLast = false,
}: {
	label: string
	timestamp: string
	isFirst?: boolean
	isLast?: boolean
}) {
	return (
		<div className="flex gap-4">
			<div className="flex flex-col items-center">
				<div className={`h-3 w-3 rounded-full bg-primary ${!isFirst ? "mt-5" : ""}`} />
				{!isLast && <div className="w-0.5 flex-1 bg-border min-h-[3rem]" />}
			</div>
			<div className={`pb-6 ${!isLast ? "" : "pb-0"}`}>
				<p className="text-sm font-medium">{label}</p>
				<p className="text-xs text-muted-foreground">
					{new Date(timestamp).toLocaleString()}
				</p>
			</div>
		</div>
	)
}
